import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Row,
    Col,
    Form,
    Button,
    Spinner,
    Alert,
    Badge,
} from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaCamera } from 'react-icons/fa';
import { getUserFromLocalStorage } from '../../utilities/user';
import { colors } from './partnerTheme';

const API_URL = process.env.REACT_APP_SERVER_URL || '';
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET =
    process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'lusuku-clou';

const PartnerProfile = () => {
    const { agent } = useSelector((state) => state.auth);
    const user = agent || getUserFromLocalStorage();
    const partnerId = user?.result?.partnerId || user?.result?._id;
    const token = user?.token;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [cloudinaryLoaded, setCloudinaryLoaded] = useState(false);

    // Load Cloudinary script
    useEffect(() => {
        if (window.cloudinary) {
            setCloudinaryLoaded(true);
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://widget.cloudinary.com/v2.0/global/all.js';
        script.async = true;
        script.onload = () => setCloudinaryLoaded(true);
        document.body.appendChild(script);
    }, []);

    // Handle logo upload via Cloudinary widget
    const handleLogoUpload = () => {
        if (!cloudinaryLoaded || !window.cloudinary) {
            toast.warn('Cloudinary is loading, please wait...');
            return;
        }

        const widget = window.cloudinary.createUploadWidget(
            {
                cloudName: CLOUDINARY_CLOUD_NAME,
                uploadPreset: CLOUDINARY_UPLOAD_PRESET,
                folder: `3pl-partners/${partnerId}`,
                maxFileSize: 500000, // 500KB
                clientAllowedFormats: ['jpg', 'jpeg', 'png', 'webp'],
                multiple: false,
                croppingAspectRatio: 1, // Force square
                transformation: [
                    { width: 100, height: 100, crop: 'fill', gravity: 'face' },
                    { quality: 'auto:good' }
                ],
                sources: ['local', 'url', 'camera'],
                showPoweredBy: false,
                styles: {
                    palette: {
                        window: '#FFFFFF',
                        windowBorder: colors.blue,
                        tabIcon: colors.blue,
                        menuIcons: colors.blue,
                        textDark: '#000000',
                        textLight: '#FFFFFF',
                        link: colors.blue,
                        action: colors.green,
                        inProgress: colors.blue,
                        complete: colors.green,
                        error: '#F44336',
                        sourceBg: '#F5F5F5',
                    },
                },
            },
            async (error, result) => {
                if (error) {
                    console.error('Cloudinary upload error:', error);
                    toast.error('Upload failed. Please try again.');
                    return;
                }

                if (!result || result.event !== 'success') return;
                if (result.event === 'success') {
                    setUploadingLogo(true);
                    try {
                        // Save logo URL to backend
                        await axios.put(
                            `${API_URL}/api/3pl-partners/${partnerId}/profile`,
                            {
                                logo: {
                                    url: result.info.secure_url,
                                    cloudinaryPublicId: result.info.public_id,
                                    uploadedAt: new Date().toISOString()
                                }
                            },
                            { headers: { Authorization: `Bearer ${token}` } }
                        );
                        toast.success('Logo uploaded successfully!');
                        fetchProfile(); // Refresh profile data
                    } catch (err) {
                        toast.error('Failed to save logo. Please try again.');
                    } finally {
                        setUploadingLogo(false);
                    }
                }
            }
        );

        widget.open();
    };

    const [formData, setFormData] = useState({
        companyName: '',
        contactPerson: { name: '', email: '', phone: '' },
        businessAddress: { street: '', city: '', district: '' },
        bankDetails: { bankName: '', accountName: '', accountNumber: '' },
    });

    useEffect(() => {
        if (partnerId && token) {
            fetchProfile();
        } else {
            setLoading(false);
            setError('Partner ID not found');
        }
    }, [partnerId, token]);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/api/3pl-partners/${partnerId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                const data = response.data.data?.partner || response.data.data;
                setProfile(data);
                setFormData({
                    companyName: data.companyName || '',
                    contactPerson: data.contactPerson || { name: '', email: '', phone: '' },
                    businessAddress: data.businessAddress || { street: '', city: '', district: '' },
                    bankDetails: data.bankDetails || { bankName: '', accountName: '', accountNumber: '' },
                });
            }
        } catch (err) {
            console.error('Error fetching profile:', err);
            // Use data from auth
            setProfile({
                companyName: user?.result?.companyName || 'Partner',
                email: user?.result?.email || '',
                status: 'active',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (section, field, value) => {
        if (section) {
            setFormData({
                ...formData,
                [section]: { ...formData[section], [field]: value },
            });
        } else {
            setFormData({ ...formData, [field]: value });
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API_URL}/api/3pl-partners/${partnerId}/profile`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('Profile updated successfully');
            setIsEditing(false);
            fetchProfile();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    // ── 2FA state ────────────────────────────────────────────────────────────
    const [twoFAEnabled, setTwoFAEnabled] = useState(false);
    const [twoFAStep, setTwoFAStep] = useState('idle'); // idle | setup | confirm | disable
    const [qrCode, setQrCode] = useState('');
    const [manualSecret, setManualSecret] = useState('');
    const [totpInput, setTotpInput] = useState('');
    const [backupCodes, setBackupCodes] = useState([]);
    const [twoFALoading, setTwoFALoading] = useState(false);
    const [twoFAError, setTwoFAError] = useState('');

    const handlePasswordChange = async () => {
        setPasswordError('');
        setPasswordSuccess('');

        if (!passwordData.currentPassword) {
            setPasswordError('Current password is required');
            return;
        }
        if (passwordData.newPassword.length < 8) {
            setPasswordError('New password must be at least 8 characters');
            return;
        }
        if (!/[A-Z]/.test(passwordData.newPassword)) {
            setPasswordError('New password must contain at least one uppercase letter');
            return;
        }
        if (!/[0-9]/.test(passwordData.newPassword)) {
            setPasswordError('New password must contain at least one number');
            return;
        }
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }

        setChangingPassword(true);
        try {
            const response = await axios.put(
                `${API_URL}/api/3pl-partners/${partnerId}/change-password`,
                {
                    currentPassword: passwordData.currentPassword,
                    newPassword: passwordData.newPassword,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                setPasswordSuccess('Password changed successfully!');
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            }
        } catch (err) {
            setPasswordError(
                err.response?.data?.error?.message || 'Failed to change password'
            );
        } finally {
            setChangingPassword(false);
        }
    };

    // ── 2FA handlers ─────────────────────────────────────────────────────────
    // Sync twoFAEnabled from profile when it loads
    useEffect(() => {
        if (profile) setTwoFAEnabled(!!profile.twoFA?.enabled);
    }, [profile]);

    const handle2FASetup = async () => {
        setTwoFAError('');
        setTwoFALoading(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/3pl-partners/2fa/setup`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setQrCode(res.data.data.qrCode);
            setManualSecret(res.data.data.secret);
            setTwoFAStep('confirm');
        } catch (err) {
            setTwoFAError(err.response?.data?.error?.message || '2FA setup failed.');
        } finally {
            setTwoFALoading(false);
        }
    };

    const handle2FAConfirm = async () => {
        if (!totpInput || totpInput.length !== 6) {
            setTwoFAError('Enter the 6-digit code from Google Authenticator.');
            return;
        }
        setTwoFAError('');
        setTwoFALoading(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/3pl-partners/2fa/confirm`,
                { totpCode: totpInput },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setBackupCodes(res.data.data.backupCodes);
            setTwoFAEnabled(true);
            setTwoFAStep('done');
            setTotpInput('');
            toast.success('Two-factor authentication enabled!');
        } catch (err) {
            setTwoFAError(err.response?.data?.error?.message || 'Invalid code.');
        } finally {
            setTwoFALoading(false);
        }
    };

    const handle2FADisable = async () => {
        if (!totpInput || totpInput.length !== 6) {
            setTwoFAError('Enter your current authenticator code to confirm.');
            return;
        }
        setTwoFAError('');
        setTwoFALoading(true);
        try {
            await axios.post(
                `${API_URL}/api/3pl-partners/2fa/disable`,
                { totpCode: totpInput },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setTwoFAEnabled(false);
            setTwoFAStep('idle');
            setTotpInput('');
            toast.success('Two-factor authentication disabled.');
        } catch (err) {
            setTwoFAError(err.response?.data?.error?.message || 'Invalid code.');
        } finally {
            setTwoFALoading(false);
        }
    };

    if (loading) {
        return (
            <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="grow" style={{ color: "rgba(104, 25, 110, 0.84)" }} />
            </div>
        );
    }

    return (
        <div className="content">
            <Row className="mb-4">
                <Col>
                    <h4 style={{ color: colors.body }}>
                        <i className="nc-icon nc-circle-09 me-2" />
                        Partner Profile
                    </h4>
                    <p style={{ color: colors.muted }}>Manage your company information</p>
                </Col>
                <Col className="text-end">
                    {!isEditing ? (
                        <Button
                            style={{ backgroundColor: colors.blue, borderColor: colors.blue }}
                            onClick={() => setIsEditing(true)}
                        >
                            <i className="nc-icon nc-settings-gear-65 me-2" />
                            Edit Profile
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="secondary"
                                className="me-2"
                                onClick={() => setIsEditing(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                style={{ backgroundColor: colors.green, borderColor: colors.green }}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? <Spinner size="sm" animation="border" /> : 'Save Changes'}
                            </Button>
                        </>
                    )}
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}

            <Row>
                {/* Company Overview */}
                <Col lg="4">
                    <Card>
                        <CardBody className="text-center py-5">
                            {/* Logo Upload Area */}
                            <div
                                style={{
                                    width: '100px',
                                    height: '100px',
                                    borderRadius: '50%',
                                    backgroundColor: profile?.logo?.url ? 'transparent' : colors.blue,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 10px',
                                    overflow: 'hidden',
                                    border: profile?.logo?.url ? `3px solid ${colors.blue}` : 'none',
                                    cursor: 'pointer',
                                    position: 'relative',
                                }}
                                onClick={handleLogoUpload}
                                title="Upload logo"
                            >
                                {uploadingLogo ? (
                                    <Spinner animation="border" size="sm" style={{ color: colors.white }} />
                                ) : profile?.logo?.url ? (
                                    <img
                                        src={profile.logo.url}
                                        alt="Partner Logo"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <i className="nc-icon nc-bus-front-12" style={{ fontSize: '40px', color: colors.white }} />
                                )}
                                {/* Upload overlay on hover */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(0,0,0,0.5)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                        borderRadius: '50%',
                                    }}
                                    className="logo-upload-overlay"
                                >
                                    <FaCamera style={{ color: colors.white, fontSize: '24px' }} />
                                </div>
                            </div>
                            <small style={{ color: colors.muted, fontSize: '11px', display: 'block', marginBottom: '10px' }}>
                                Click to upload logo (100×100px)
                            </small>
                            <style>{`
                                [title="Upload logo"]:hover .logo-upload-overlay {
                                    opacity: 1 !important;
                                }
                            `}</style>
                            <h4 style={{ color: colors.body }}>{profile?.companyName}</h4>
                            <p style={{ color: colors.muted }}>{profile?.email}</p>
                            <Badge
                                bg=""
                                style={{
                                    fontSize: '13px',
                                    fontWeight: '400',
                                    color: '#fff',
                                    padding: '8px 20px',
                                    borderRadius: '20px',
                                    backgroundColor: profile?.status === 'active' ? '#00C853' : '#FFC107',
                                    boxShadow: profile?.status === 'active'
                                        ? '0 4px 4px #c80064ff'
                                        : '0 4px 12px rgba(255, 193, 7, 0.4)',
                                    letterSpacing: '0.2px',
                                    textTransform: 'capitalize'
                                }}
                            >
                                ● {profile?.status?.toUpperCase()}
                            </Badge>

                            <hr className="my-4" />

                            <div className="text-start">
                                <p className="mb-2">
                                    <strong>Partner ID:</strong><br />
                                    <small style={{ color: colors.muted }}>{partnerId?.slice(-12)}</small>
                                </p>
                                <p className="mb-2">
                                    <strong>Partnership Tier:</strong><br />
                                    <Badge
                                        bg=""
                                        style={{
                                            backgroundColor: profile?.partnershipTier === 'gold' ? '#FFD700'
                                                : profile?.partnershipTier === 'silver' ? '#C0C0C0'
                                                    : '#e57c13ff', // bronze
                                            color: profile?.partnershipTier === 'gold' ? '#333'
                                                : profile?.partnershipTier === 'silver' ? '#333'
                                                    : '#fff',
                                            fontWeight: '600',
                                            padding: '6px 14px',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {profile?.partnershipTier || 'BRONZE'}
                                    </Badge>
                                </p>
                                <p className="mb-2">
                                    <strong>Commission Rate:</strong><br />
                                    <span style={{ color: colors.green, fontWeight: 'bold' }}>
                                        {profile?.commissionRate || 18}%
                                    </span>
                                </p>
                                {/* Read-only login email — admin must update via Edit 3PL Partner */}
                                <p className="mb-0">
                                    <strong>Login Email:</strong><br />
                                    <small style={{ color: colors.muted }}>{profile?.email || '—'}</small>
                                    <br />
                                    <small style={{ color: '#9CA3AF', fontSize: '10px' }}>
                                        Contact a Lusuku admin to update your login email.
                                    </small>
                                </p>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                {/* Profile Details */}
                <Col lg="8">
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h5" style={{ color: colors.body }}>
                                Company Details
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            <Form>
                                <h6 style={{ color: colors.blue }} className="mb-3">Contact Person</h6>
                                <Row>
                                    <Col md="4">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.contactPerson.name}
                                                onChange={(e) => handleChange('contactPerson', 'name', e.target.value)}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md="4">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Contact Email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                value={formData.contactPerson.email}
                                                onChange={(e) => handleChange('contactPerson', 'email', e.target.value)}
                                                disabled={!isEditing}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                For communications only — not used for login
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md="4">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Phone</Form.Label>
                                            <Form.Control
                                                type="tel"
                                                value={formData.contactPerson.phone}
                                                onChange={(e) => handleChange('contactPerson', 'phone', e.target.value)}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-4" />

                                <h6 style={{ color: colors.blue }} className="mb-3">Business Address</h6>
                                <Row>
                                    <Col md="4">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Street</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.businessAddress.street}
                                                onChange={(e) => handleChange('businessAddress', 'street', e.target.value)}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md="4">
                                        <Form.Group className="mb-3">
                                            <Form.Label>City</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.businessAddress.city}
                                                onChange={(e) => handleChange('businessAddress', 'city', e.target.value)}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md="4">
                                        <Form.Group className="mb-3">
                                            <Form.Label>District</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.businessAddress.district}
                                                onChange={(e) => handleChange('businessAddress', 'district', e.target.value)}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-4" />

                                <h6 style={{ color: colors.blue }} className="mb-3">Bank Details</h6>
                                <Row>
                                    <Col md="4">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Bank Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.bankDetails.bankName}
                                                onChange={(e) => handleChange('bankDetails', 'bankName', e.target.value)}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md="4">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Account Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.bankDetails.accountName}
                                                onChange={(e) => handleChange('bankDetails', 'accountName', e.target.value)}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md="4">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Account Number</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.bankDetails.accountNumber}
                                                onChange={(e) => handleChange('bankDetails', 'accountNumber', e.target.value)}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>

                    {/* Change Password Card */}
                    <Card className="mt-3">
                        <CardHeader>
                            <CardTitle tag="h5" style={{ color: colors.body }}>
                                <i className="nc-icon nc-key-25 me-2" />
                                Change Password
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            {passwordError && (
                                <Alert variant="danger" onClose={() => setPasswordError('')} dismissible>
                                    {passwordError}
                                </Alert>
                            )}
                            {passwordSuccess && (
                                <Alert variant="success" onClose={() => setPasswordSuccess('')} dismissible>
                                    {passwordSuccess}
                                </Alert>
                            )}
                            <Row>
                                <Col md="4">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Current Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            placeholder="Enter current password"
                                            value={passwordData.currentPassword}
                                            onChange={(e) =>
                                                setPasswordData({ ...passwordData, currentPassword: e.target.value })
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md="4">
                                    <Form.Group className="mb-3">
                                        <Form.Label>New Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            placeholder="Enter new password"
                                            value={passwordData.newPassword}
                                            onChange={(e) =>
                                                setPasswordData({ ...passwordData, newPassword: e.target.value })
                                            }
                                        />
                                        <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                            Min 8 chars, 1 uppercase, 1 number
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md="4">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Confirm New Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            placeholder="Confirm new password"
                                            value={passwordData.confirmPassword}
                                            onChange={(e) =>
                                                setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Button
                                style={{
                                    backgroundColor: colors.blue,
                                    borderColor: colors.blue,
                                }}
                                onClick={handlePasswordChange}
                                disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                            >
                                {changingPassword ? (
                                    <><Spinner size="sm" animation="border" className="me-2" /> Changing...</>
                                ) : (
                                    'Change Password'
                                )}
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ── 2FA Card ──────────────────────────────────────────────────── */}
            <Row className="mt-3">
                <Col>
                    <Card>
                        <CardHeader className="d-flex align-items-center justify-content-between">
                            <div>
                                <CardTitle tag="h5" style={{ margin: 0 }}>
                                    🔐 Two-Factor Authentication (2FA)
                                </CardTitle>
                                <small style={{ color: colors.muted }}>
                                    Add an extra layer of security using Google Authenticator.
                                </small>
                            </div>
                            <Badge style={{
                                backgroundColor: twoFAEnabled ? colors.green : '#6b7280',
                                color: '#fff', padding: '6px 12px', fontSize: 12,
                            }}>
                                {twoFAEnabled ? '✓ Enabled' : 'Disabled'}
                            </Badge>
                        </CardHeader>
                        <CardBody>
                            {twoFAError && (
                                <Alert variant="danger" style={{ fontSize: 13 }}>{twoFAError}</Alert>
                            )}

                            {/* ── IDLE STATE ── */}
                            {twoFAStep === 'idle' && !twoFAEnabled && (
                                <div>
                                    <p style={{ color: colors.muted, fontSize: 14 }}>
                                        Protect your account by requiring a code from Google Authenticator
                                        every time you log in. Free to set up — just install the app on your phone.
                                    </p>
                                    <div style={{
                                        background: '#F0FDF4', border: '1px solid #BBF7D0',
                                        borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13
                                    }}>
                                        Install <strong>Google Authenticator</strong> from the{' '}
                                        <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noreferrer">Play Store</a>{' '}or{' '}
                                        <a href="https://apps.apple.com/app/google-authenticator/id388497605" target="_blank" rel="noreferrer">App Store</a>, then click below.
                                    </div>
                                    <Button
                                        style={{ backgroundColor: colors.green, borderColor: colors.green }}
                                        onClick={() => { setTwoFAStep('setup'); handle2FASetup(); }}
                                        disabled={twoFALoading}
                                    >
                                        {twoFALoading
                                            ? <><Spinner size="sm" animation="border" className="me-2" />Setting up...</>
                                            : '🔐 Enable Two-Factor Authentication'
                                        }
                                    </Button>
                                </div>
                            )}

                            {/* ── SCAN QR ── */}
                            {twoFAStep === 'confirm' && (
                                <Row>
                                    <Col md="5" className="text-center">
                                        <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>1. Scan this QR code</p>
                                        {qrCode && (
                                            <img src={qrCode} alt="2FA QR Code"
                                                style={{ width: 180, height: 180, border: '3px solid #006202', borderRadius: 12 }}
                                            />
                                        )}
                                        <p style={{ fontSize: 11, color: colors.muted, marginTop: 8 }}>
                                            Can't scan? Enter this code manually in the app:
                                        </p>
                                        <code style={{
                                            background: '#F3F4F6', padding: '4px 8px', borderRadius: 4,
                                            fontSize: 12, letterSpacing: 2, wordBreak: 'break-all'
                                        }}>
                                            {manualSecret}
                                        </code>
                                    </Col>
                                    <Col md="7">
                                        <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>2. Enter the 6-digit code</p>
                                        <p style={{ fontSize: 13, color: colors.muted }}>
                                            After scanning, open Google Authenticator and enter the code shown for Lusuku.
                                        </p>
                                        <Form.Control
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            placeholder="123456"
                                            value={totpInput}
                                            onChange={e => setTotpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            style={{ letterSpacing: 8, fontSize: 22, fontWeight: 700, maxWidth: 200, marginBottom: 16 }}
                                        />
                                        <div className="d-flex gap-2">
                                            <Button
                                                style={{ backgroundColor: colors.green, borderColor: colors.green }}
                                                onClick={handle2FAConfirm}
                                                disabled={twoFALoading || totpInput.length !== 6}
                                            >
                                                {twoFALoading
                                                    ? <><Spinner size="sm" animation="border" className="me-1" />Verifying...</>
                                                    : 'Activate 2FA'
                                                }
                                            </Button>
                                            <Button variant="secondary" onClick={() => { setTwoFAStep('idle'); setTwoFAError(''); setTotpInput(''); }}>
                                                Cancel
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {/* ── DONE — show backup codes (one time) ── */}
                            {twoFAStep === 'done' && backupCodes.length > 0 && (
                                <div>
                                    <Alert variant="warning" style={{ fontSize: 13 }}>
                                        <strong>⚠️ Save these backup codes now.</strong> They are shown only once.
                                        Use them to log in if you lose access to your authenticator app.
                                    </Alert>
                                    <div style={{
                                        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                                        gap: 8, background: '#F9FAFB', padding: 16,
                                        borderRadius: 8, marginBottom: 16,
                                    }}>
                                        {backupCodes.map((code, i) => (
                                            <code key={i} style={{
                                                background: '#fff', border: '1px solid #E5E7EB',
                                                borderRadius: 6, padding: '6px 10px',
                                                fontSize: 13, fontWeight: 600, textAlign: 'center',
                                                letterSpacing: 2,
                                            }}>{code}</code>
                                        ))}
                                    </div>
                                    <Button variant="outline-secondary" size="sm"
                                        onClick={() => navigator.clipboard.writeText(backupCodes.join('\n')).then(() => toast.info('Backup codes copied!'))}
                                    >
                                        Copy All
                                    </Button>
                                    <Button size="sm" className="ms-2" variant="secondary"
                                        onClick={() => { setTwoFAStep('idle'); setBackupCodes([]); }}
                                    >
                                        Done
                                    </Button>
                                </div>
                            )}

                            {/* ── ENABLED — management options ── */}
                            {twoFAEnabled && twoFAStep === 'idle' && (
                                <div>
                                    <p style={{ color: colors.muted, fontSize: 14 }}>
                                        Your account is protected with Google Authenticator. You'll need your
                                        authenticator code each time you log in.
                                    </p>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => { setTwoFAStep('disable'); setTwoFAError(''); setTotpInput(''); }}
                                    >
                                        Disable 2FA
                                    </Button>
                                </div>
                            )}

                            {/* ── DISABLE confirmation ── */}
                            {twoFAStep === 'disable' && (
                                <div>
                                    <Alert variant="warning" style={{ fontSize: 13 }}>
                                        Disabling 2FA will make your account less secure. Enter your
                                        current authenticator code to confirm.
                                    </Alert>
                                    <Form.Control
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        placeholder="6-digit code"
                                        value={totpInput}
                                        onChange={e => setTotpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        style={{ letterSpacing: 8, fontSize: 22, fontWeight: 700, maxWidth: 200, marginBottom: 16 }}
                                    />
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="danger"
                                            onClick={handle2FADisable}
                                            disabled={twoFALoading || totpInput.length !== 6}
                                        >
                                            {twoFALoading
                                                ? <><Spinner size="sm" animation="border" className="me-1" />Disabling...</>
                                                : 'Confirm Disable 2FA'
                                            }
                                        </Button>
                                        <Button variant="secondary" onClick={() => { setTwoFAStep('idle'); setTwoFAError(''); setTotpInput(''); }}>
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PartnerProfile;
